import { getCurrencySymbol } from '@gameday/common/functions/currency'
import { download } from '@gameday/common/functions/documents'
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

import theme, { rem } from '../../utils/theme'
import TextOnlyButton from '../TextOnlyButton'

const StyledSummaryItem = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  border: ${theme.borders.default};
  border-radius: 4px;
  padding: ${rem(10)} ${rem(16)} ${rem(10)} ${rem(14)};

  ${props =>
    props.hasImage === true &&
    css`
      padding: ${rem(10)} ${rem(16)} ${rem(10)} ${rem(10)};
    `}

  & + & {
    margin-top: ${rem(9)};
  }

  ${theme.breakpoints.down('xs')} {
    flex-flow: row wrap;
  }
`

const StyledImage = styled.div`
  align-self: flex-start;
  border: 1px solid ${theme.colors.border};
  margin-right: ${rem(10)};
`

const StyledLeft = styled.div`
  display: flex;
  flex-direction: column;

  ${props =>
    props.hasImage === true &&
    css`
      flex-direction: row;
    `}

  ${theme.breakpoints.down('xs')} {
    width: 100%;
  }

  ${theme.breakpoints.up('sm')} {
    flex: 1;
  }
`

const StyledContent = styled.div`
  flex-direction: column;
`

const StyledRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: right;

  ${theme.breakpoints.down('xs')} {
    width: 100%;
    margin-top: ${rem(10)};
    padding-top: ${rem(10)};
    border-top: ${theme.borders.default};
  }
`

const StyledTop = styled.div``

const StyledItem = styled.div`
  font-size: ${theme.typography.fontSizeTinyish};
  color: ${`var(--gameday-body-textColorLight, ${theme.body.textColorLight})`};

  ${props =>
    props.flag === 'general' &&
    css`
      font-size: ${theme.typography.fontSizeSm};
      color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};
    `}

  ${props =>
    props.flag === 'surcharge' &&
    css`
      font-size: ${theme.typography.fontSizeTinyish};
      font-style: italic;
      margin-top: ${rem(3)};
    `}

  ${props =>
    props.flag === 'discount' &&
    css`
      font-size: ${theme.typography.fontSizeTinyish};
      font-style: italic;
      font-weight: ${theme.typography.fontWeightBolder};
      margin-top: ${rem(3)};
      color: ${theme.status.successColor};
    `}
`

const StyledQty = styled.div`
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};
`

const StyledPrice = styled.div`
  font-weight: ${theme.typography.fontWeightBolder};
  color: ${`var(--gameday-body-textColorDark, ${theme.body.textColorDark})`};

  ${props =>
    props.isDiscounted &&
    css`
      font-size: ${theme.typography.fontSizeMicro};
      color: var(--gameday-body-textColor, ${theme.body.textColor});
      text-decoration: line-through;
    `}

  > span {
    color: ${theme.body.textColorExtraLight};
  }
`

const StyledRemoveButton = styled.div`
  // margin-bottom: ${rem(-2)};
`

const SummaryItem = ({
  items,
  formType,
  showRemove,
  removeDisabled,
  onClickRemove,
  id,
  type,
  image,
  deliveryMethod,
  formData,
  productData,
  productDiscount,
  productPromoCode,
  displayPriceAsZero = false,
}) => {
  const isDiscounted = items && items[0].currentValue !== items[0].totalPrice
  const hasImage = image && type === 'Add-on'
  const [productImage, setProductImage] = useState('')
  const { productAttributes, attributeId, attributeValue, optionIds } = productData

  useEffect(() => {
    if (image) {
      download({
        file: image,
        info: {
          loggedInUser: formData.userId || 'not specified',
          objectId: formData.profileId,
          objectName: 'productImage',
          component: formData.pageName || 'Image Download',
          formId: formData.form,
          action: 'download',
          target: 'Product Image',
          funcName: 'ProductImageDownload',
          application: formData.application,
        },
        url: formData.downloadEndpoint,
      }).then(response => {
        setProductImage(response)
      })
    }
  }, [image])

  const { discountName, discountAmount } = productDiscount || {}

  const { promoCodeAmount, promoCodeRule } = productPromoCode || {}
  const { name: promoCodeName } = promoCodeRule || {}
  const { totalPrice = 0 } = items[0] || {}

  return (
    <StyledSummaryItem hasImage={hasImage}>
      <StyledLeft hasImage={hasImage}>
        {hasImage && (
          <StyledImage>
            <img src={productImage} alt={items[0].key} width="65px" />
          </StyledImage>
        )}
        <StyledContent>
          <StyledItem flag={items[0].flag}>
            <strong>{items[0].key}</strong>
          </StyledItem>
          {productAttributes &&
            optionIds?.length > 1 &&
            productAttributes.map((group, index) => (
              <StyledItem key={group.productAttributeId} flag={items[0].flag}>
                <strong>{group.productAttributeName}: </strong>
                {group.items.find(i => i.id === optionIds[index])?.name}
              </StyledItem>
            ))}
          {productAttributes && attributeId && (
            <StyledItem flag={items[0].flag}>
              <strong>{productAttributes[0].productAttributeName}: </strong>
              {productAttributes[0].items.filter(i => i.id === attributeId)[0]?.name}
            </StyledItem>
          )}
          {productAttributes && attributeValue && (
            <StyledItem flag={items[0].flag}>
              <strong>{productAttributes[0].productAttributeName}: </strong>
              {attributeValue}
            </StyledItem>
          )}
          {totalPrice > 0 && productDiscount && (
            <StyledItem flag="discount">
              {discountName} - {`${getCurrencySymbol(items[0].currencyCode)}${Number(discountAmount).toFixed(2)} `}
            </StyledItem>
          )}
          {totalPrice > 0 && productPromoCode && promoCodeAmount && (
            <StyledItem flag="discount">
              {promoCodeName} - {`${getCurrencySymbol(items[0].currencyCode)}${Number(promoCodeAmount).toFixed(2)} `}
            </StyledItem>
          )}
          {deliveryMethod && (
            <StyledItem>
              <strong>Delivery:</strong> {deliveryMethod}
            </StyledItem>
          )}
          <StyledItem flag={items[2].flag}>
            {items[2].key} {items[2].value}
          </StyledItem>
        </StyledContent>
      </StyledLeft>
      <StyledRight>
        <StyledTop>
          {((items[0].quantity && formType === 'event') || type === 'Add-on') && (
            <StyledQty>Qty: {items[0].quantity}</StyledQty>
          )}
          {isDiscounted && (
            <StyledPrice isDiscounted>
              {`${getCurrencySymbol(items[0].currencyCode)} ${Number(items[0].totalPrice).toFixed(2)} `}
              <span>{items[0].currencyCode}</span>
            </StyledPrice>
          )}
          <StyledPrice>
            {Number(items[0].currentValue) === 0 && items[0].currentValue === items[0].totalPrice
              ? displayPriceAsZero
                ? `${getCurrencySymbol(items[0].currencyCode)} 0.00 `
                : 'FREE'
              : `${getCurrencySymbol(items[0].currencyCode)} ${Number(items[0].currentValue).toFixed(2)} `}
            {Number(items[0].currentValue) === 0 &&
            items[0].currentValue === items[0].totalPrice &&
            !displayPriceAsZero ? null : (
              <span>{items[0].currencyCode}</span>
            )}
          </StyledPrice>
        </StyledTop>
        {showRemove && (
          <StyledRemoveButton>
            <TextOnlyButton
              label="remove item"
              aria-label="Remove Item"
              onClick={onClickRemove}
              id={`remove-${id}`}
              data-testid="removeItem"
              disabled={!!removeDisabled}
            />
          </StyledRemoveButton>
        )}
      </StyledRight>
    </StyledSummaryItem>
  )
}

export default SummaryItem
